import { RouteProp, useNavigation, useRoute } from '@react-navigation/core'
import { debounce, groupBy, upperCase, upperFirst } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dimensions, Keyboard, KeyboardEvent } from 'react-native'
import { useSelector } from 'react-redux'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ReactNativeFile } from '@src/utils/image'
import { AppStackParamList } from '@src/navigation/types'
import { Feature, useActionBottomSheet, useFeatureFlag } from '@src/components'
import {
  addonLimitsSelector,
  dexcomResearchConnectionSelector,
  dexcomSteloConnectionSelector,
  onboardingSkippedSelector,
  uiStatesSelector,
  userSelector,
  uxSensorModeKindSelector,
} from '@src/selectors/app'
import {
  DexcomConnectionKind,
  SensorModel,
  SubscriptionStatus,
  UiStateNames,
  UserExperienceSensorModeKind,
} from '@src/types'
import { ADDON_MAX_QUANTITY } from '@src/screens/Marketplace/constants/constants'
import Device from '@config/device'
import { sensorSelector } from '@src/selectors/sensor'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { Apollo, App, Debug } from '@src/config'
import { chooseFromLibrary } from './image'
import { useSnack } from './navigatorContext'
import { CAMERA_DENIED_SNACK_MESSAGE } from './camera'

export const useAllowedAddonQuantity = () => {
  const addonLimits = useSelector(addonLimitsSelector)
  const addonLimitByProductId = groupBy(addonLimits, 'productId')

  const getAllowedAddonQuantity = (
    addonProductId: string,
  ): { allowedAddonQuantity: number; addonLimit: number | null } => {
    const addonLimit = addonLimitByProductId[addonProductId][0] as typeof addonLimits[0] | undefined
    return {
      allowedAddonQuantity:
        addonLimit && typeof addonLimit.limit === 'number'
          ? addonLimit.limit - addonLimit.purchasedAmount
          : ADDON_MAX_QUANTITY,
      addonLimit: addonLimit?.limit ?? null,
    }
  }

  return {
    addonLimitByProductId,
    getAllowedAddonQuantity,
  }
}

export const usePrevious = <T>(value: T) => {
  const ref = useRef<T | undefined>(undefined)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export const useCancelRoute = () => {
  const route = useRoute<
    RouteProp<
      AppStackParamList,
      | 'AddActivity'
      | 'AddMeasurement'
      | 'AddMeal'
      | 'EditMeal'
      | 'EditAccountDetails'
      | 'CustomIngredient'
      | 'IngredientSearch'
      | 'Marketplace'
    >
  >()
  const { params } = route
  const cancel = params?.cancel

  return {
    route,
    cancel,
  }
}

export const useCancelModal = ({
  goBack,
  isModified,
  itemName,
  title,
  cancelText,
  confirmText,
}: {
  goBack: () => void
  isModified: boolean
  itemName?: string
  title?: string
  cancelText?: string
  confirmText?: string
}) => {
  const navigation = useNavigation()
  const { route, cancel } = useCancelRoute()

  useEffect(() => {
    if (cancel) {
      navigation.setParams({ cancel: undefined })
      goBack()
    }
  }, [cancel, goBack, navigation])

  const buttonItemText = itemName
    ? itemName
        .split(' ')
        .map((word) => upperFirst(word))
        .join(' ')
    : ''

  const openCancelModal = useCallback(() => {
    if (!isModified) {
      goBack()
      return
    }

    navigation.navigate('CancelModal', {
      title: title || `Do you really want to cancel the new ${itemName}?`,
      cancelText: cancelText || `No, Continue New ${buttonItemText}`,
      confirmText: confirmText || `Yes, Cancel New ${buttonItemText}`,
      parentScreen: route.name,
    })
  }, [
    buttonItemText,
    cancelText,
    confirmText,
    goBack,
    isModified,
    itemName,
    navigation,
    route.name,
    title,
  ])

  return openCancelModal
}

export const usePhotoUpload = (callback: (photo: ReactNativeFile) => void) => {
  const navigation = useNavigation()
  const route = useRoute<RouteProp<AppStackParamList, 'AddMeal' | 'EditMeal'>>()
  const params = route.params || { photo: undefined }
  const { photo } = params
  const showSnack = useSnack()

  useLazyScreenPreload('Camera')

  useEffect(() => {
    if (!photo) {
      return
    }

    navigation.setParams({ photo: undefined })

    callback(photo)
  }, [navigation, photo, callback])

  const launchCameraScreen = () =>
    navigation.navigate('Camera', { parentScreen: route.name, parentParams: route.params })

  const onCameraPermissionDenied = () => {
    showSnack(CAMERA_DENIED_SNACK_MESSAGE, null, 'warning')
  }

  const launchImageLibrary = () => chooseFromLibrary(callback, onCameraPermissionDenied)

  return { launchCameraScreen, launchImageLibrary }
}

export const useKeyboardVisibility = (optimistic: boolean): boolean => {
  const [isVisible, setIsVisible] = useState(false)

  const showEventName = optimistic ? 'keyboardWillShow' : 'keyboardDidShow'
  const hideEventName = optimistic ? 'keyboardWillHide' : 'keyboardDidHide'

  useEffect(() => {
    const showEventSubscription = Keyboard.addListener(showEventName, () => setIsVisible(true))
    const hideEventSubscription = Keyboard.addListener(hideEventName, () => setIsVisible(false))

    return () => {
      showEventSubscription.remove()
      hideEventSubscription.remove()
    }
  }, [showEventName, hideEventName])

  return isVisible
}

export const useKeyboardHeight = () => {
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  useEffect(() => {
    const onKeyboardDidShow = (event: KeyboardEvent) =>
      setKeyboardHeight(event.endCoordinates.height)
    const onKeyboardDidHide = () => setKeyboardHeight(0)

    const showListener = Keyboard.addListener('keyboardDidShow', onKeyboardDidShow)
    const hideListener = Keyboard.addListener('keyboardDidHide', onKeyboardDidHide)

    return () => {
      showListener.remove()
      hideListener.remove()
    }
  }, [])

  return keyboardHeight
}

export const useAvailableScreenHeight = () => {
  const insets = useSafeAreaInsets()
  const keyboardHeight = useKeyboardHeight()

  return Dimensions.get('window').height - keyboardHeight - insets.top - insets.bottom
}

export const useDebouncedCallback = (
  ...[callback, delay, options]: Parameters<typeof debounce>
) => {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  return useMemo(() => {
    return debounce((...args) => callbackRef.current(...args), delay, options)
  }, [delay, options])
}

export const useAllowSkipPurchaseFlow = () => {
  const allowSkipPurchaseFlowFeatureFlag = useFeatureFlag(Feature.AllowSkipPurchaseFlow)

  return allowSkipPurchaseFlowFeatureFlag
}

export const useShouldShowPurchaseFlow = () => {
  const onboardingSkipped = useSelector(onboardingSkippedSelector)

  const uiStates = useSelector(uiStatesSelector)

  const checkoutCompletedUiState = uiStates.find(
    (uiState) => uiState.name === UiStateNames.CheckoutCompleted,
  )

  const checkoutCompleted = checkoutCompletedUiState?.value || false

  return !onboardingSkipped && !checkoutCompleted
}

export const useAllowSignInWithApple = () => Device.ios

export const useAllowSignInWithGoogle = () => true

export const useAllowDietitianSupport = () => {
  const user = useSelector(userSelector)

  const ongoingDietitianSubscription =
    user && 'ongoingDietitianSubscription' in user ? user.ongoingDietitianSubscription : undefined

  if (!ongoingDietitianSubscription) {
    return false
  }

  const ongoingDietitianSubscriptionStatus =
    'status' in ongoingDietitianSubscription ? ongoingDietitianSubscription.status : undefined

  return (
    ongoingDietitianSubscriptionStatus === SubscriptionStatus.Active ||
    ongoingDietitianSubscriptionStatus === SubscriptionStatus.Trialing
  )
}

export const useIsActiveDexcom = () => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  return [
    UserExperienceSensorModeKind.ActiveResearchDexcom,
    UserExperienceSensorModeKind.ActivePrescriptionDexcomG7,
    UserExperienceSensorModeKind.OwnSensorDexcomG7,
    UserExperienceSensorModeKind.OwnSensorDexcomG6,
    UserExperienceSensorModeKind.DexcomStelo,
  ].includes(uxSensorModeKind as UserExperienceSensorModeKind)
}

export const useIsDexcomSteloSensorMode = () => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  return uxSensorModeKind === UserExperienceSensorModeKind.DexcomStelo
}

export const useHasSyncedSteloRealtime = () => {
  const uiStates = useSelector(uiStatesSelector)
  const steloRealtimeSyncedUiState = uiStates.find(
    (uiState) => uiState.name === UiStateNames.SyncedSteloRealtime,
  )

  return steloRealtimeSyncedUiState?.value || false
}

export const useMarkedForTransitionToSteloRealtime = () => {
  const uiStates = useSelector(uiStatesSelector)

  return uiStates.some(
    (uiState) =>
      uiState.name === UiStateNames.SteloRealtimeApiUpgradeBanner ||
      uiState.name === UiStateNames.SteloShowMigrationBanner,
  )
}

export const useShowDexcomSteloLogo = () => {
  const sensor = useSelector(sensorSelector)
  return sensor?.modelEnum === SensorModel.DexcomStelo
}

export const useDexcomConnectionKind = () => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  if (uxSensorModeKind === UserExperienceSensorModeKind.DexcomStelo) {
    return DexcomConnectionKind.Stelo
  }

  // includes G6 and G7 sensors
  return DexcomConnectionKind.Research
}

export const useDexcomConnection = (kind?: DexcomConnectionKind) => {
  const defaultConnectionKind = useDexcomConnectionKind()
  const dexcomConnectionKind = kind || defaultConnectionKind
  return useSelector(
    dexcomConnectionKind === DexcomConnectionKind.Stelo
      ? dexcomSteloConnectionSelector
      : dexcomResearchConnectionSelector,
  )
}

// This hook returns the sensor name based on the provided sensor model
// or the UX sensor mode kind if no model is provided.
export const useDexcomConnectionSensorName = (sensorModel?: SensorModel) => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)
  switch (sensorModel) {
    case SensorModel.DexcomG6:
      return 'Dexcom G6'
    case SensorModel.DexcomG7:
      return 'Dexcom G7'
    case SensorModel.DexcomStelo:
      return 'Dexcom Stelo'
    default:
      switch (uxSensorModeKind) {
        case UserExperienceSensorModeKind.OwnSensorDexcomG6:
          return 'Dexcom G6'
        case UserExperienceSensorModeKind.DexcomStelo:
          return 'Stelo'
        default:
          return 'Dexcom G7'
      }
  }
}

export const useUserInitials = () => {
  const { firstName = '', lastName = '' } = useSelector(userSelector) ?? {}

  const initials = useMemo(() => {
    return `${upperCase(firstName[0])}${upperCase(lastName[0])}`
  }, [firstName, lastName])

  return initials
}

export const useShowIntercomDisabledModal = () => {
  const showActionBottomSheet = useActionBottomSheet()

  const showModal = useCallback(() => {
    if (Debug.shouldEnableIntercom()) {
      return
    }

    let modalBody = 'Intercom is not available.'
    if (Device.web) {
      modalBody = 'Intercom is not available on web.'
    } else if (App.build.beta && Apollo.endpointEnv === 'staging') {
      modalBody = 'Intercom is not available in Beta + Staging. Please use Alpha + Staging instead.'
    } else if ((App.build.development || App.build.alpha) && Apollo.endpointEnv !== 'staging') {
      modalBody = 'Intercom is only available in Staging for Alpha/Dev builds.'
    }

    showActionBottomSheet({
      title: 'Intercom is disabled',
      body: modalBody,
      primaryButton: {
        text: 'Dismiss',
      },
    })
  }, [showActionBottomSheet])

  return showModal
}
